/**
 * @fileoverview One growing location, drawn the way a plant card is: the name first, the
 * location's own photo strip and tools, its tiles and their trays, and then
 * what a location has that a plant does not: the plants growing in it, and
 * what happened there lately.
 *
 * Laid out as a plant card is: the name as the Edit button with a small pencil at its end (CardNameRow, B158; no ⋯ menu); under the
 * picture and This week, the plant card's own tiles (CardTiles, B158 jobs 6 and 7): row 1 is Share, filled, with its opens count, and
 * Public, the tile switch; row 2 is To-dos and Notes, half width each. The photo's own tools are on the photo (add a photo, edit this
 * photo) and its dots centred under it.
 *
 * The tiles open trays under them, one at a time (the card holds which, as the plant card does). The To-dos tray is the gardener's own
 * to-dos about this bed (D50, OwnTodosRow; This week leaves them out, so a bed's to-do shows once) and, last, the "To-do" and "Plant"
 * buttons (E25: Plant something opens with this bed chosen). The Notes tray is the bed's journal, with "A note for every plant..."
 * first (a DoorRow, shown while the bed has plants). The To-dos tile's count and its terracotta tint (a to-do due today) are the
 * server's, on the bed in the brief (bed.todos); the card works out neither.
 *
 * Its plants are a heading with a count, open to begin with and folding on a phone: each plant a row with its photo, stage and
 * week, then "Add a new plant here", which opens the Plants pop-up with this bed preset (BedPlants; D78: its photo row reads seed
 * packets too), and last "Clear the bed for the season..." (a DoorRow).
 *
 * D56 (owner, 2026-10-16: the card as it was, "where did all that go?"): under the name, what the bed is in one line (the server's
 * bed_line); and, when the bed has jobs, under the picture and over the tiles, This week: its own jobs as Today's cards, counting
 * only its plants (BedJobs; the first three on the list, then "2 more jobs", all of them on the open bed). It sat under the name
 * until the design critique (owner, "1a"): there it pushed the picture and the plants off a phone's first screen. Lately is a
 * short timeline under Plants; a bed note's row in it opens the Notes tray.
 *
 * The photo carousel, the photo dialogs and the notes section are the plant
 * card's own, pointed at the location instead of a plant. Its photos are
 * drawn in the server's order (newest first), so the strip opens on the
 * newest; with none, the picture is the server's cover (bug B2), drawn by the same PlantCardPhoto as a one-picture strip (B158 job 8).
 *
 * The Seed vault (D69, features/seed-orders; the server's is_vault, held and latest): a seed-packet icon before its
 * name, today's mail in a line under it, the seeds waiting for a tap above its plants (HeldSeeds), and, while it has no
 * plant growing and nothing waiting, the garden's seed order address where the plants would be (VaultEmpty).
 *
 * Where: LocationGrid, the cards density of /beds (the default), the
 * open bed, /beds/:id (BedOpen: the same card, the page's width), and the
 * pop-up a grid tile or list row opens (B157, BedQuickLook; its head row is
 * marked data-card-head, inside CardNameRow, so the dialog's close X stays clear of the pencil).
 * W24 Job H: everything the card counts or lists comes on the bed from the
 * one list of beds (GET /locations?status=1): its growing plants and their
 * number (plants, plant_count), Lately (lately: the season diary's own newest
 * rows for the bed) and the Share count and the Public switch's state
 * (sharing, handed to the switch so it reads nothing of its own; when the
 * server sends null, the card and the switch read the share route as before).
 * The card filters and counts nothing.
 *
 * API: none of its own for the count, Lately or the switch (the bed list's);
 * the photo, notes, share and packet (BedPlants) children make their own (photos under
 * /locations/:id/photos, notes under /locations/:id/journal).
 * Settings: CARD_MULTI_COLUMN_MEDIA_QUERY (one column folds
 * Plants)
 * (config/tunables/garden); the bed's status (an archived bed is drawn
 * faded and read-only) and sheltered flag.
 * Words (en/es): locations.card.* (header.edit, plants.list.title,
 * plants.list.addNew), locations.bed.noteMenu, locations.bed.clearMenu,
 * locations.sheltered.*, plants.card.* (photos.add,
 * photos.editThis, locations.open.week,
 * actions.share, tiles.*, public.visits), plants.share.noPhotosYetLocation, season.lately,
 * season.addedShort, season.removedShort, season.aPhoto, season.thisBed.
 * Watch out: a plant's photo in the bed's share goes under its id made
 * negative (the two photo tables number apart), as the share route keeps
 * them; the cover stands in as "photo 0" until the bed has photos of its own.
 *
 * @module LocationCard
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, ChevronRight, CloudOff, Pencil, Plus, StickyNote } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';
import { useLocationsContext } from '@/shared/context/LocationsContextDefinition';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { Card, CardContent, CardDescription, CardHeader } from '@/shared/components/ui/card';
import { SectionHeading } from '@/shared/components/ui/SectionHeading';
import { cn } from '@/lib/utils';
import { locationSubject } from '@/shared/api/subject';
import type { Location } from '@/shared/api/locations';
import type { PlantPhoto } from '@/shared/types/photo';
import { DateTime } from '@/shared/utils/dateUtils';
import type { SeasonDiaryRow } from '@/shared/api/season';
import { PlantCardPhoto } from '@/features/plant-list/components/card/PlantCardPhoto';
import { PhotoButton } from '@/shared/components/ui/PhotoButton';
import { CardNameRow } from '@/shared/components/ui/CardNameRow';
import { DoorRow } from '@/shared/components/ui/DoorRow';
import { AddFirstPhoto } from '@/features/plant-list/components/card/AddFirstPhoto';
import { CardTiles, type CardTray } from '@/features/plant-list/components/card/CardTiles';
import { PhotoUploadDialog } from '@/features/plant-list/components/dialogs/PhotoUploadDialog';
import { BedPlants } from './BedPlants';
import { BedJobs } from './BedJobs';
import type { TodoMarks } from '@/features/today/lib/useTodoMarks';
import type { BriefBed, BriefTodo } from '@/shared/api/todayBrief';
import { SharePlantDialog } from '@/features/plant-list/components/dialogs/SharePlantDialog';
import { useShareCount } from '@/shared/hooks/useShareCount';
import { shareWords } from '@/shared/lib/plantName';
import { useLocationPhotos } from '../lib/useLocationPhotos';
import {
  CARD_MULTI_COLUMN_MEDIA_QUERY,
} from '@/config/tunables/garden';
import { shareDraftKey, useResumeShare } from '@/features/plant-list/lib/shareDraft';
import { bedCaptionStarter } from '@/features/plant-list/lib/captionStarter';
import { useGardenSeason } from '@/shared/hooks/useTodayBrief';
// D69: the Seed vault's icon, its address card when empty, and the seeds waiting for a tap (all from the server's is_vault and held).
import { VaultEmpty, VaultIcon } from '@/features/seed-orders/VaultBits';
import { HeldSeeds } from '@/features/seed-orders/HeldSeeds';
import { vaultView } from '@/features/seed-orders/lib/vaultView';
import { useBringIntoView } from '@/shared/hooks/useBringIntoView';

/** The location to draw, and what its controls open. */
interface LocationCardProps {
  /** The bed, as the beds list has it (its plants, photos and notes). */
  location: Location;
  /** Opens the edit dialog for this location. */
  onEdit: (location: Location) => void;
  /** Opens one of the plants growing here, full size. */
  onOpenPlant: (plantObjId: number) => void;
  /** The newest few things that happened in this location; the bed's own `lately` (the bed list's) when not given. */
  lately?: SeasonDiaryRow[];
  /** Opens the plant form with this location already chosen. */
  onAddPlant?: () => void;
  /** Opens "Note for all": one note on the plants growing here. */
  onNoteAll?: () => void;
  /** Archives the plants growing here at the end of a season. */
  onClearAll?: () => void;
  /** The one to-do list (the brief's todo), for the bed's This week; none without a brief. */
  todo?: BriefTodo | null;
  /** The bed's place in it (todo.beds): its jobs and its part of each; none, no This week. */
  bed?: BriefBed | null;
  /** Ticking, from the page's useTodoMarks. */
  marks?: TodoMarks;
  /** This week shows so many jobs, then "2 more jobs" opening the bed (the list); all when absent (the open bed). */
  jobsShown?: number;
}

/** Status value the backend uses for an active location. */
const STATUS_ACTIVE = 1;

/**
 * One location as a card: its name as the Edit button (a pencil), its description,
 * Public and Share, its photos or cover with their tools on them, its notes,
 * its plants with the whole-bed actions (or "Add a plant" while it has none),
 * and what happened lately.
 *
 * @param props - LocationCardProps.
 */
export const LocationCard: React.FC<LocationCardProps> = ({
  location, onEdit, onOpenPlant, lately = location.lately, onAddPlant, onNoteAll, onClearAll, todo = null, bed = null, marks, jobsShown,
}) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { mysqlUser } = useAuth();
  const { refreshLocations } = useLocationsContext();
  const userId = mysqlUser?.user_id ?? 0;
  /** The bed as a subject, for the photo, notes and share calls (one object while the id stays). */
  const subject = React.useMemo(() => locationSubject(location.id), [location.id]);
  const isInactive = location.status !== STATUS_ACTIVE;
  /** The Seed vault's extras (D69): the icon, the address card while empty, the held seeds' block. */
  const vault = vaultView(location);

  /*
   * The plants growing here now, as the bed list sends them (only those, W24
   * Job H; the removed ones are in the Archive, and this bed's season still
   * shows them), and how many there are (the server's count).
   */
  const plants = location.plants;
  const plantCount = location.plant_count;

  // The location's own photos, as the plant card keeps a plant's.
  const photos = useLocationPhotos(location);
  const hasPhotos = photos.photos.length > 0;
  /*
   * Until a location has photos of its own, its cover (the picture it opened
   * with, or the one from Edit) stands in as "photo 0": what a share of it
   * carries, and what its public page shows.
   */
  const cover = React.useMemo<PlantPhoto | null>(() => (location.signed_photo_url ? {
    photo_id: 0, filename: 'cover', date_taken: '', date_uploaded: '',
    signed_photo_url: location.signed_photo_url, cardUrl: location.signed_photo_url, thumbUrl: location.signed_photo_url,
  } : null), [location.signed_photo_url]);
  /*
   * What a share of it can carry: its cover (the picture at the top of the
   * card), its own photos, and each of its plants' photos, the ones its plant
   * rows show. A plant's photo goes under its id made negative, as the share
   * route keeps them (the two tables number apart). The cover was offered
   * only until the location had photos of its own, and its plants never.
   */
  const { plants: garden } = usePlantsContext();
  const { plantPhotos, plantByPhoto } = React.useMemo(() => {
    const here = new Set(plants.map((plant) => plant.plant_obj_id));
    const byPhoto = new Map<number, { name: string; variety: string; plantObjId: number }>();
    const list: PlantPhoto[] = [];
    for (const plant of garden ?? []) {
      if (!here.has(plant.plant_obj_id)) continue;
      const first = ((plant.photos ?? []) as unknown as PlantPhoto[])[0];
      if (!first) continue;
      list.push({ ...first, photo_id: -first.photo_id });
      // The frame's two words for the plant: the crop's name and the plant's own words (lib/plantName shareWords).
      byPhoto.set(-first.photo_id, { ...shareWords(plant), plantObjId: plant.plant_obj_id });
    }
    return { plantPhotos: list, plantByPhoto: byPhoto };
  }, [garden, plants]);
  /** The plant a photo in the share is of, for its own details on the picture. */
  const plantOf = React.useCallback((photoId: number) => plantByPhoto.get(photoId) ?? null, [plantByPhoto]);
  const shareable = [...(cover ? [cover] : []), ...photos.photos, ...plantPhotos];
  const [isUploadOpen, setIsUploadOpen] = React.useState(false);
  const [isEditPhotoOpen, setIsEditPhotoOpen] = React.useState(false);
  /** The words of the note written with a photo, if it has one. */
  const noteFor = React.useCallback(
    (photo: PlantPhoto): string | null => (photo.note_id ? location.notes?.find((n) => n.note_id === photo.note_id)?.content ?? null : null),
    [location.notes],
  );
  /** Reads the beds again after a photo or note here changed. */
  const refresh = React.useCallback(() => { void refreshLocations(); }, [refreshLocations]);

  // Plants fold on a phone (one column), open to begin with; where the card cannot fold it is always open.
  const isMultiColumn = useMediaQuery(CARD_MULTI_COLUMN_MEDIA_QUERY);
  const canFold = !isMultiColumn;
  const [plantsChosen, setPlantsChosen] = React.useState(true);
  const plantsOpen = !canFold || plantsChosen;
  /** The Plants fold, brought into view when the gardener opens it (B153); the trays are brought into view by CardTiles. */
  const plantsFoldRef = useBringIntoView(plantsOpen);
  /** The tray open under the tiles (one at a time): the to-dos, the notes, or none. */
  const [tray, setTray] = React.useState<CardTray>(null);

  /** True once the cover picture failed to load: the first-photo invitation is drawn instead. */
  const [photoFailed, setPhotoFailed] = React.useState(false);
  /** The picture drawn while the bed has no photos of its own: the server's cover (its stored cover then), as a dateless photo. */
  const picture = location.cover?.signed_photo_url ?? null;
  const coverOnly = React.useMemo<PlantPhoto[] | null>(() => (picture ? [{
    photo_id: 0, filename: 'cover', date_taken: '', date_uploaded: '',
    signed_photo_url: picture, cardUrl: picture, thumbUrl: picture,
  }] : null), [picture]);
  React.useEffect(() => setPhotoFailed(false), [picture]);
  /** What the shared photo frame draws: the bed's own photos, else its cover alone (unless it failed), else nothing (the invitation below). */
  const shownPhotos = hasPhotos ? photos.photos : photoFailed ? null : coverOnly;

  // Share, with how often its links were opened, as on a plant card.
  const [isShareOpen, setIsShareOpen] = React.useState(false);
  // The garden's season for the caption starter: the server's (useGardenSeason), read once the Share screen opens.
  const gardenSeason = useGardenSeason(isShareOpen);
  // A share of this bed in progress when the tab was reloaded: its sheet opens again where it was.
  useResumeShare(shareDraftKey('location', location.id), () => setIsShareOpen(true));
  // How often the bed's links were opened: the bed list's sharing (no read of its own, W24 Job H); only when the server
  // could not read it (sharing null) does the card read the share route itself, as before.
  const { opens: readOpens } = useShareCount(subject, location.sharing === null && !isInactive && shareable.length > 0);
  const opens = location.sharing ? location.sharing.visits : readOpens;
  /** Nothing to share yet (no cover, no photo of its own, no plant photo): the tiles are dim and a tap says what comes first. */
  const nothingToShare = shareable.length === 0;
  /** The share studio's own words for a bed with nothing to share yet. */
  const noPhotosWords = t('plants.share.noPhotosYetLocation');

  return (
    <>
      <Card className={cn('relative flex h-full w-full flex-col overflow-hidden rounded-sm shadow-lg', isInactive && 'opacity-70')}>
        {/* The name first, as on a plant card. */}
        <CardHeader tight>
          {/* The name is the Edit button, a pencil at its end (CardNameRow, B158); it owns data-card-head, which BedQuickLook
              keeps clear of the pop-up's close X. An archived bed is edited from the Archive, so its row has no pencil. */}
          <CardNameRow
            before={vault.icon ? <VaultIcon className="h-5 w-5" /> : undefined}
            name={location.name}
            onEdit={isInactive ? undefined : () => onEdit(location)}
            ariaLabel={t('locations.card.header.edit', { name: location.name })}
          />
          {/* What the bed is, in one line (the server's bed_line, D56): "Raised bed · full sun · heavy frost cloth on hoops". */}
          {location.bed_line && <p className="text-sm text-muted-foreground">{location.bed_line}</p>}
          {/* D69: today's mail on the Seed vault, while it is from today (the server's line). */}
          {location.latest && <p className="text-sm text-muted-foreground">{location.latest.text}</p>}
          {/* Two lines of fixed height, whatever a location has to say, so the
              pictures line up across a row of cards: the count and description
              on one line, cut short if long; the sheltered note, or its space. */}
          {(location.description || (location.sheltered && !location.bed_line_says_sheltered)) && (
            <CardDescription className="flex flex-col gap-0.5">
              {location.description && <span className="block truncate">{location.description}</span>}
              {/* Not said twice: the server says when the bed's line is already "Sheltered" (bed_line_says_sheltered). */}
              {location.sheltered && !location.bed_line_says_sheltered && (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground" title={t('locations.sheltered.long')}>
                  <CloudOff className="h-3 w-3" aria-hidden="true" />
                  {t('locations.sheltered.short')}
                </span>
              )}
            </CardDescription>
          )}
        </CardHeader>

        {/* The location's own pictures: the strip once it has some; its cover until then, drawn by the same
            PlantCardPhoto as the one-picture strip (B158 job 8); or the same "take a photo" tile a plant starts with. */}
        {shownPhotos ? (
          <PlantCardPhoto
            photos={shownPhotos}
            selected={hasPhotos ? photos.selected : 0}
            onSelect={photos.select}
            isLoading={hasPhotos && photos.isLoading}
            noteFor={noteFor}
            plantName={location.name}
            onEditPhoto={isInactive || !hasPhotos ? undefined : () => setIsEditPhotoOpen(true)}
            editHint={false}
            peek
            onImageError={hasPhotos ? undefined : () => setPhotoFailed(true)}
            actions={isInactive ? undefined : (
              <>
                {/* Adding a photo, the more common, first. */}
                <PhotoButton label={t('plants.card.photos.add')} onClick={() => setIsUploadOpen(true)}>
                  {/* A camera with a plus: add a picture. */}
                  <span className="relative" aria-hidden="true">
                    <Camera className="h-3.5 w-3.5" />
                    <Plus className="absolute -right-1 -top-1 h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                </PhotoButton>
                {hasPhotos && (
                  <PhotoButton label={t('plants.card.photos.editThis')} onClick={() => setIsEditPhotoOpen(true)}>
                    <Pencil className="h-3 w-3" aria-hidden="true" />
                  </PhotoButton>
                )}
              </>
            )}
          />
        ) : !isInactive ? (
          <AddFirstPhoto onTakePhoto={() => setIsUploadOpen(true)} varietyName={location.name} />
        ) : null}

        {/* This week (D56): the bed's own jobs, counting only its plants, when it has any (BedJobs).
            Under the picture, over Notes and Plants (owner, 2026-10-16, "1a" of the design critique: the
            jobs had pushed the picture and the plants off the first screen). */}
        {todo && bed && marks && bed.keys.length > 0 && (
          <section aria-label={t('locations.open.week')} className="pb-1">
            <SectionHeading right={bed.count}>{t('locations.open.week')}</SectionHeading>
            <div className="px-4">
              <BedJobs todo={todo} bed={bed} marks={marks} limit={jobsShown} />
            </div>
          </section>
        )}

        {/* The tiles, as the plant card's (CardTiles, B158 jobs 6 and 7): row 1 is Share, filled, two-thirds, with how often its links
            were opened, and Public, one-third, the tile switch (both dim with the share studio's words when nothing is shareable);
            row 2 is To-dos (the brief's count, terracotta when one is due today) and Notes, half width each. Each opens a tray
            under the tiles, one at a time: To-dos holds the bed's own to-dos and the To-do and Plant buttons; Notes opens with
            "A note for every plant..." (its DoorRow) above the journal. An archived bed has the Notes tile alone, read-only. */}
        <CardTiles
          subject={subject}
          name={location.name}
          hasPhotos={!nothingToShare}
          archived={isInactive}
          showPublic
          opens={opens}
          sharing={location.sharing ?? undefined}
          dueToday={bed?.todos.dueToday ?? false}
          todoCount={bed?.todos.open ?? 0}
          notes={location.notes}
          plantPublic={false}
          tray={tray}
          onTray={setTray}
          onShare={() => setIsShareOpen(true)}
          noPhotosWords={noPhotosWords}
          shareName={location.name}
          onNotesChanged={refresh}
          notesLead={plantCount > 0 && onNoteAll && !isInactive ? (
            <DoorRow icon={<StickyNote />} onClick={onNoteAll}>{t('locations.bed.noteMenu')}</DoorRow>
          ) : undefined}
        />

        {/* What a location has that a plant does not: its plants, a row like
            Notes that folds on a phone (open to begin with: they are what the
            card is for), and what happened here lately. The end-of-season jobs
            are in Notes and at the foot of Plants (DoorRows). */}
        <SectionHeading
          count={plantCount > 0 ? plantCount : undefined}
          fold={{ open: plantsOpen, onToggle: () => setPlantsChosen(!plantsOpen), canFold }}
        >
          {t('locations.card.plants.list.title')}
        </SectionHeading>
        {plantsOpen && (
          <CardContent ref={plantsFoldRef} className="space-y-1 px-4 pb-3 pt-0">
            {/* D69: the seeds waiting for a tap, above the vault's plants; an empty vault shows its address card. */}
            {vault.held && !isInactive && <HeldSeeds count={location.held ?? 0} />}
            {vault.addressCard && !isInactive && <VaultEmpty />}
            <BedPlants location={location} plants={plants} onOpenPlant={onOpenPlant} onAddPlant={onAddPlant} onClearAll={onClearAll} readOnly={isInactive} />
          </CardContent>
        )}

        {lately.length > 0 && (
          <>
          <SectionHeading>{t('season.lately')}</SectionHeading>
          <CardContent className="px-4 pb-4 pt-1">
            {/* A short timeline, lighter than the list above it. */}
            <ol className="space-y-0">
              {lately.map((event, index) => (
                <li key={event.key} className="grid grid-cols-[3.25rem_0.75rem_1fr] items-start gap-2">
                  <span className="pt-px text-right text-xs tabular-nums text-muted-foreground">
                    {DateTime.fromISO(event.day).setLocale(language).toLocaleString({ month: 'short', day: 'numeric' })}
                  </span>
                  {/* The dot, and the line on down to the next one, however tall this entry wraps. */}
                  <span className="relative flex justify-center self-stretch" aria-hidden="true">
                    <span className="relative z-10 mt-1.5 h-2 w-2 rounded-full bg-primary" />
                    {index < lately.length - 1 && <span className="absolute bottom-0 top-3.5 w-0.5 bg-border" />}
                  </span>
                  <button
                    type="button"
                    onClick={() => { if (event.plantObjId !== null) onOpenPlant(event.plantObjId); else setTray('notes'); }}
                    className="min-w-0 pb-2.5 text-left text-sm leading-snug hover:underline"
                  >
                    <span className="font-semibold">{event.plantName}</span>
                    {' · '}
                    {event.kind === 'added'
                      ? t('season.addedShort')
                      : event.kind === 'removed'
                        ? t('season.removedShort')
                        : event.kind === 'stage'
                          ? event.stageName
                          : event.note || t('season.aPhoto')}
                  </button>
                </li>
              ))}
            </ol>
            <Link
              to={`/season?bed=${location.id}`}
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              {t('season.thisBed')}
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </CardContent>
          </>
        )}
      </Card>

      <SharePlantDialog
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        plantObjId={location.id}
        subject={subject}
        // The sheet is titled by the variety and letters the name once when both match.
        name={location.name}
        variety={location.name}
        photos={shareable}
        startPhotoId={hasPhotos ? photos.photos[photos.selected]?.photo_id ?? null : cover?.photo_id ?? null}
        plantOf={plantOf}
        plantCount={plantCount}
        onAddPhoto={() => setIsUploadOpen(true)}
        captionStarter={isShareOpen ? bedCaptionStarter(location.name, plantCount, t, gardenSeason) : ''}
      />

      <PhotoUploadDialog
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        userId={userId}
        plantObjId={location.id}
        subject={subject}
        onPhotoUploaded={(photo) => { photos.received(photo); refresh(); }}
      />
      {photos.current && (
        <PhotoUploadDialog
          isOpen={isEditPhotoOpen}
          onClose={() => setIsEditPhotoOpen(false)}
          userId={userId}
          plantObjId={location.id}
          subject={subject}
          editMode
          existingPhoto={photos.current}
          onPhotoUploaded={(photo) => { photos.received(photo); refresh(); }}
          onPhotoDeleted={(photoId) => { photos.removed(photoId); refresh(); }}
        />
      )}
    </>
  );
};
